import { useEffect, useState } from "react";
import { adminApi } from "../../api/adminApi";
import type { AppUser, Role } from "../../api/types";
import { DataTable } from "../../components/DataTable";
import { ConfirmDialog } from "../../components/ConfirmDialog";

const ROLES: Role[] = ["OWNER", "ADMIN", "DESIGNER", "VIEWER"];

export default function UserManager() {
  const [users, setUsers] = useState<AppUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [email, setEmail] = useState("");
  const [fullName, setFullName] = useState("");
  const [inviteRoles, setInviteRoles] = useState<Role[]>(["VIEWER"]);
  const [inviting, setInviting] = useState(false);
  const [invited, setInvited] = useState<{ email: string; temporaryPassword: string; reset?: boolean } | null>(null);
  const [pendingReset, setPendingReset] = useState<AppUser | null>(null);

  function load() {
    setLoading(true);
    adminApi.users
      .list()
      .then(setUsers)
      .catch((err) => setError(err instanceof Error ? err.message : "Failed to load users"))
      .finally(() => setLoading(false));
  }

  useEffect(load, []);

  function toggleRole(role: Role) {
    setInviteRoles((prev) => (prev.includes(role) ? prev.filter((r) => r !== role) : [...prev, role]));
  }

  async function handleInvite() {
    if (!email.trim() || inviteRoles.length === 0) return;
    setInviting(true);
    setError(null);
    try {
      const result = await adminApi.users.invite(email.trim(), fullName.trim(), inviteRoles);
      setInvited({ email: result.user.email, temporaryPassword: result.temporaryPassword });
      setEmail("");
      setFullName("");
      setInviteRoles(["VIEWER"]);
      load();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to invite user");
    } finally {
      setInviting(false);
    }
  }

  function copyInvited() {
    if (invited) {
      navigator.clipboard
        ?.writeText(`Email: ${invited.email}\nPassword: ${invited.temporaryPassword}`)
        .catch(() => undefined);
    }
  }

  async function updateRoles(user: AppUser, role: Role, checked: boolean) {
    const next = checked ? [...user.roles, role] : user.roles.filter((r) => r !== role);
    if (next.length === 0) return;
    try {
      await adminApi.users.setRoles(user.id, next);
      load();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to update roles");
    }
  }

  async function handleResetPassword() {
    if (!pendingReset) return;
    const target = pendingReset;
    setPendingReset(null);
    try {
      const res = await adminApi.users.resetPassword(target.id);
      setInvited({ email: res.email, temporaryPassword: res.temporaryPassword, reset: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to reset the password");
    }
  }

  async function toggleUserStatus(user: AppUser) {
    try {
      if (user.status === "ACTIVE") {
        await adminApi.users.disable(user.id);
      } else {
        await adminApi.users.enable(user.id);
      }
      load();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to update user status");
    }
  }

  return (
    <div>
      <div className="page-header">
        <h1>Users</h1>
      </div>
      {error && <div className="error-text">{error}</div>}

      <div className="page-panel">
        <h3>Invite user</h3>
        <p className="help-text">
          A strong temporary password is generated automatically. It's shown once below — send it
          to the client and have them change it after first login.
        </p>
        <div className="form-grid">
          <div className="form-row">
            <label>Email</label>
            <input value={email} onChange={(e) => setEmail(e.target.value)} type="email" />
          </div>
          <div className="form-row">
            <label>Full name</label>
            <input value={fullName} onChange={(e) => setFullName(e.target.value)} />
          </div>
        </div>
        <div className="form-row">
          <label>Roles</label>
          <div style={{ display: "flex", gap: 12 }}>
            {ROLES.map((r) => (
              <label key={r} style={{ fontWeight: 400 }}>
                <input
                  type="checkbox"
                  checked={inviteRoles.includes(r)}
                  onChange={() => toggleRole(r)}
                  style={{ marginRight: 4 }}
                />
                {r}
              </label>
            ))}
          </div>
        </div>
        <button type="button" className="btn-primary" onClick={handleInvite} disabled={inviting}>
          {inviting ? "Inviting..." : "Send invite"}
        </button>
      </div>

      {invited && (
        <div className="page-panel" style={{ borderColor: "#f59e0b" }}>
          <h4>{invited.reset ? "Password reset — hand these credentials to them now" : "User created — hand these credentials to them now"}</h4>
          <p className="help-text">
            This is the only time the password is shown. It can't be retrieved again.
          </p>
          <div className="code-block">
            Email: {invited.email}
            {"\n"}Password: {invited.temporaryPassword}
          </div>
          <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
            <button type="button" className="btn-secondary" onClick={copyInvited}>
              Copy
            </button>
            <button type="button" className="btn-secondary" onClick={() => setInvited(null)}>
              Dismiss
            </button>
          </div>
        </div>
      )}

      <div className="page-panel">
        {loading ? (
          <p>Loading...</p>
        ) : (
          <DataTable
            rows={users}
            rowKey={(u) => u.id}
            emptyMessage="No users yet."
            columns={[
              { key: "email", header: "Email" },
              { key: "fullName", header: "Name" },
              {
                key: "roles",
                header: "Roles",
                render: (u) => (
                  <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                    {ROLES.map((r) => (
                      <label key={r} style={{ fontWeight: 400, fontSize: 12 }}>
                        <input
                          type="checkbox"
                          checked={u.roles.includes(r)}
                          onChange={(e) => updateRoles(u, r, e.target.checked)}
                          style={{ marginRight: 3 }}
                        />
                        {r}
                      </label>
                    ))}
                  </div>
                ),
              },
              {
                key: "status",
                header: "Status",
                render: (u) => (
                  <span className={u.status === "ACTIVE" ? "badge badge-published" : "badge badge-archived"}>
                    {u.status}
                  </span>
                ),
              },
              {
                key: "actions",
                header: "",
                render: (u) => (
                  <div style={{ display: "flex", gap: 6 }}>
                    <button type="button" className="btn-secondary" onClick={() => setPendingReset(u)}>
                      Reset password
                    </button>
                    <button
                      type="button"
                      className={u.status === "ACTIVE" ? "btn-danger" : "btn-secondary"}
                      onClick={() => toggleUserStatus(u)}
                    >
                      {u.status === "ACTIVE" ? "Disable" : "Enable"}
                    </button>
                  </div>
                ),
              },
            ]}
          />
        )}
      </div>

      <ConfirmDialog
        open={!!pendingReset}
        title="Reset this password"
        message={pendingReset ? `Reset the password for ${pendingReset.email}? Their current password stops working immediately and you'll be shown a temporary one to give them.` : ""}
        confirmLabel="Reset password"
        danger
        onConfirm={handleResetPassword}
        onCancel={() => setPendingReset(null)}
      />
    </div>
  );
}
